import { useEffect } from 'react';
import {
  isIosPhone,
  isPhoneDevice,
  supportsElementFullscreen,
  supportsOrientationLock,
} from '../features/install/utils.js';

export async function lockPortraitOrientation() {
  if (!supportsOrientationLock()) return;

  try {
    await screen.orientation?.lock?.('portrait-primary');
  } catch {
    // Orientation lock is not supported or requires fullscreen on this device.
  }
}

export function unlockScreenOrientation() {
  if (!supportsOrientationLock()) return;

  try {
    screen.orientation?.unlock?.();
  } catch {
    // Ignore unsupported browsers.
  }
}

/**
 * @param {HTMLVideoElement | null | undefined} video
 */
export function enterIosVideoFullscreen(video) {
  if (!video) return false;

  try {
    if (typeof video.webkitEnterFullscreen === 'function') {
      video.webkitEnterFullscreen();
      return true;
    }

    if (typeof video.webkitSetPresentationMode === 'function') {
      video.webkitSetPresentationMode('fullscreen');
      return true;
    }
  } catch {
    // iOS may reject if not called from a user gesture.
  }

  return false;
}

/**
 * Lock portrait on mount; re-lock when `active` becomes false.
 * @param {boolean} active
 */
export function usePortraitOrientationLock(active = true) {
  useEffect(() => {
    if (!active || !supportsOrientationLock()) return undefined;

    lockPortraitOrientation();

    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        lockPortraitOrientation();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      lockPortraitOrientation();
    };
  }, [active]);
}

/**
 * Android: native element fullscreen + orientation unlock.
 * iOS: orientation unlock is not available; rely on overlay + optional video fullscreen.
 * @param {import('react').RefObject<HTMLElement | null>} containerRef
 * @param {import('react').RefObject<{ enterNativeFullscreen?: () => boolean } | null>} playerRef
 */
export function useVideoFullscreenOrientation(containerRef, playerRef) {
  useEffect(() => {
    if (!isPhoneDevice()) return undefined;

    const container = containerRef.current;
    unlockScreenOrientation();

    const enterNativeFullscreen = async () => {
      if (isIosPhone()) {
        playerRef.current?.enterNativeFullscreen?.();
        return;
      }

      if (!supportsElementFullscreen() || !container?.requestFullscreen) return;

      try {
        await container.requestFullscreen();
      } catch {
        // User gesture or platform may block automatic fullscreen.
      }
    };

    const timer = window.setTimeout(enterNativeFullscreen, 0);

    const handleFullscreenChange = () => {
      if (document.fullscreenElement === container) {
        unlockScreenOrientation();
        return;
      }

      lockPortraitOrientation();
    };

    if (!isIosPhone()) {
      document.addEventListener('fullscreenchange', handleFullscreenChange);
    }

    return () => {
      window.clearTimeout(timer);

      if (!isIosPhone()) {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);

        if (document.fullscreenElement === container) {
          document.exitFullscreen?.().catch(() => {});
        }
      }

      lockPortraitOrientation();
    };
  }, [containerRef, playerRef]);
}
